<template>
	<div class="modal-card ops_by_users_card">
		<header class="modal-card-head ops-head">
			<div class="ops-head__intro">
				<p class="ops-eyebrow">Panel analista</p>
				<h3 class="ops-title">Operaciones del usuario</h3>
			</div>
			<button class="ops-close" aria-label="Cerrar Modal" @click="$emit('close')">×</button>
		</header>
		<section class="modal-card-body ops_body">
			<!-- Selector de usuario: una píldora por miembro de la empresa. -->
			<div class="ops-section">
				<p class="ops-label">Usuario</p>
				<div class="users_pills" role="navigation" aria-label="Usuarios de la empresa">
					<a class="user_pill" role="button" tabindex="0" :class="{
						'is-selected': selectedUser && selectedUser.user_id == user.user_id
					}" @click="() => selectUser(user)" @keydown.enter.prevent="selectUser(user)"
						v-for="user in allUsers" v-bind:key="user.user_id">
						{{ user.user_email }}
					</a>
				</div>
			</div>

			<!-- Sin selección: invitación a actuar (reemplaza el blur + tabla
				de guiones del modal original). -->
			<div v-if="!selectedUser" class="ops-empty">
				Elegí un usuario para ver y editar sus operaciones.
			</div>

			<template v-else>
				<div class="ops-section">
					<p class="ops-label">Acceso</p>
					<InputWithValidation id="urlLooker" name="urlLooker" label="URL del reporte Looker"
						v-model="urlLooker" rules="required" />
					<div class="tgl-grid">
						<label class="tgl">
							<input type="checkbox" v-model="isActive" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Puede ingresar a la plataforma</span>
						</label>
						<label class="tgl">
							<input type="checkbox" v-model="noHasSIIMsg" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">No mostrar el aviso de conectar el SII</span>
						</label>
					</div>
				</div>

				<div class="ops-section">
					<p class="ops-label">Módulos</p>
					<div class="tgl-grid">
						<label class="tgl">
							<input type="checkbox" v-model="hasConciliator" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Conciliador</span>
						</label>
						<label class="tgl">
							<input type="checkbox" v-model="hasReport" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Reporte Looker</span>
						</label>
						<label class="tgl">
							<input type="checkbox" v-model="hasClassification" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Clasificador</span>
						</label>
						<label class="tgl">
							<input type="checkbox" v-model="hasFinancialReport" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Reporte en línea</span>
						</label>
						<label class="tgl">
							<input type="checkbox" v-model="hasFinanciamiento" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Financiamiento</span>
						</label>
					</div>
				</div>

				<!-- Phi: la card se enciende en morado al activarla (mismos
					tokens --ap-phi del pill del panel). Cupos: vacío = default
					global; techo = 2× el default (el backend lo hace cumplir). -->
				<div class="ops-section">
					<p class="ops-label">Phi — chat IA</p>
					<div class="phi-card" :class="{ 'is-on': hasAiChat }">
						<label class="tgl tgl--phi">
							<input type="checkbox" v-model="hasAiChat" />
							<span class="tgl__track" aria-hidden="true"><span class="tgl__thumb"></span></span>
							<span class="tgl__text">Tiene Phi activado</span>
						</label>
						<div v-if="hasAiChat" class="phi-quotas">
							<div class="phi-field">
								<label class="phi-field__label" for="ai_daily_limit">Tokens por día</label>
								<input id="ai_daily_limit" class="input" type="number" min="0"
									:max="aiChatDailyMax" v-model="aiChatDailyLimit"
									placeholder="Default global" />
								<p v-if="aiChatDailyMax" class="phi-field__max">
									máx. {{ formatTokens(aiChatDailyMax) }}
								</p>
							</div>
							<div class="phi-field">
								<label class="phi-field__label" for="ai_weekly_limit">Tokens por semana</label>
								<input id="ai_weekly_limit" class="input" type="number" min="0"
									:max="aiChatWeeklyMax" v-model="aiChatWeeklyLimit"
									placeholder="Default global" />
								<p v-if="aiChatWeeklyMax" class="phi-field__max">
									máx. {{ formatTokens(aiChatWeeklyMax) }}
								</p>
							</div>
							<p class="phi-note">
								Vacío usa el cupo default global. El máximo por usuario es 2× el default.
							</p>
						</div>
					</div>
				</div>

				<div class="ops-section">
					<p class="ops-label">Tipos de contraparte</p>
					<p class="ops-hint">Los tipos que el usuario puede ver en la plataforma.</p>
					<div class="cpchips">
						<label class="cpchip" v-for="counterpart in typeOptions" :key="counterpart.value">
							<input type="checkbox" v-model="counterpart.active" />
							<span class="cpchip__body">{{ counterpart.label }}</span>
						</label>
					</div>
				</div>
			</template>
		</section>
		<footer class="modal-card-foot ops-foot">
			<span class="ops-foot__note" v-if="selectedUser">Los cambios se aplican al grabar.</span>
			<span class="ops-foot__spacer" aria-hidden="true"></span>
			<button class="button" @click="$emit('close')" aria-label="Cerrar Modal" role="button">
				Volver
			</button>
			<button class="button is-primary" :disabled="loading || selectedUser == null" role="button"
				@click="updateUserOperations()">
				Grabar
			</button>
		</footer>
	</div>
</template>

<script>
import { getUsersByCompany, updateUserOperations } from "@/api/carteraAnalista";
import InputWithValidation from "@/components/inputs/InputWithValidation";
import { notificate } from "@/utils/message";
export default {
	name: "ChangeOperationsByUsers",
	data() {
		return {
			loading: false,
			selectedUser: null,
			urlLooker: "",
			hasConciliator: false,
			hasClassification: false,
			hasFinancialReport: false,
			hasReport: false,
			noHasSIIMsg: false,
			isActive: false,
			hasFinanciamiento: false,
			hasAiChat: false,
			aiChatDailyLimit: "",
			aiChatWeeklyLimit: "",
			aiChatDailyMax: null,
			aiChatWeeklyMax: null,
			allUsers: [],
			typeOptions: []
		};
	},
	components: {
		InputWithValidation
	},
	methods: {
		formatTokens(value) {
			return Number(value).toLocaleString("es-CL");
		},
		selectUser(user) {
			this.selectedUser = user
			this.urlLooker = user.url_report
			this.hasConciliator = user.has_conciliator
			this.hasClassification = user.has_classificator
			this.hasFinancialReport = user.has_financial_report
			this.hasReport = user.has_embedded_report
			this.hasFinanciamiento = user.has_financiamiento
			this.hasAiChat = user.has_ai_chat
			// null = default global → input vacío (el placeholder lo dice).
			this.aiChatDailyLimit = user.ai_chat_daily_token_limit == null
				? ""
				: user.ai_chat_daily_token_limit
			this.aiChatWeeklyLimit = user.ai_chat_weekly_token_limit == null
				? ""
				: user.ai_chat_weekly_token_limit
			// Techo del override (2× el default global); null si el backend
			// aún no lo manda (desfase de deploy) → sin validación local.
			this.aiChatDailyMax = user.ai_chat_daily_token_max || null
			this.aiChatWeeklyMax = user.ai_chat_weekly_token_max || null
			this.noHasSIIMsg = user.no_sii_message
			this.isActive = user.is_active

			const userValidCounterparts = [...(this.selectedUser.user_valid_counterparts || [])]
			// Lista vacía = el usuario ve TODOS los tipos (misma semántica que
			// classification.js / DashboardClassificator) — se refleja con
			// todas las alternativas marcadas, no desmarcadas.
			const seesAll = userValidCounterparts.length === 0
			this.typeOptions = this.typeOptions.map(type => {
				return {
					...type,
					active: seesAll || userValidCounterparts.includes(type.value)
				};
			});
		},
		exceedsAiChatMax() {
			const exceeds = (value, max) =>
				value !== "" && max != null && Number(value) > max
			if (exceeds(this.aiChatDailyLimit, this.aiChatDailyMax)) {
				return `El cupo diario supera el máximo permitido (${this.formatTokens(this.aiChatDailyMax)} tokens, 2× el default).`
			}
			if (exceeds(this.aiChatWeeklyLimit, this.aiChatWeeklyMax)) {
				return `El cupo semanal supera el máximo permitido (${this.formatTokens(this.aiChatWeeklyMax)} tokens, 2× el default).`
			}
			return null
		},
		updateUserOperations() {
			const maxError = this.exceedsAiChatMax()
			if (maxError) {
				notificate(maxError, "is-danger is-light")
				return
			}
			const reqUserOps = {
				user_id: this.selectedUser.user_id,
				url_report: this.urlLooker,
				has_conciliator: this.hasConciliator,
				has_classificator: this.hasClassification,
				has_financial_report: this.hasFinancialReport,
				has_embedded_report: this.hasReport,
				no_sii_message: this.noHasSIIMsg,
				is_active: this.isActive,
				has_financiamiento: this.hasFinanciamiento,
				has_ai_chat: this.hasAiChat,
				// "" (input vacío) = volver al default global (null en la BD).
				ai_chat_daily_token_limit: this.aiChatDailyLimit === ""
					? null
					: Number(this.aiChatDailyLimit),
				ai_chat_weekly_token_limit: this.aiChatWeeklyLimit === ""
					? null
					: Number(this.aiChatWeeklyLimit),
			}
			let counterpartTypes = []
			this.typeOptions.map(type=>{
				if(type.active){
					counterpartTypes.push(type.value)
				}
			})
			reqUserOps.counterpart_types = counterpartTypes
			updateUserOperations(this.idCompany, reqUserOps).then(() => {
			}).finally(() => {
				this.$emit("close");
			})
		}
	},
	props: ["idCompany"],
	created() {
		getUsersByCompany(this.idCompany).then(({ data }) => {
			this.allUsers = data
		})
		this.typeOptions = [
			{ value: "Cliente", label: "🤝 Cliente", active: false },
			{ value: "Proveedor", label: "💼 Proveedor", active: false },
			{ value: "Honorario", label: "🕒 Honorario", active: false },
			{ value: 'Trabajador', label: '🧑‍💻 Trabajador', active: false },
			{ value: 'Internacional', label: '🌍 Internacional', active: false },
			{ value: 'Recibo', label: '🧾 Recibo', active: false },
			{ value: 'Finiquito', label: '📄 Finiquito', active: false },
			{ value: 'Nota de Venta', label: '💵 Nota de Ingreso', active: false },
		];
	},
};
</script>
<style scoped lang="scss">
// ============================================================================
// Operaciones del usuario — mismo sistema del Panel Analista (var(--ap-*),
// elements/_analyst_theme.scss): secciones con eyebrow separadas por
// hairlines, switches de instrumento, y la card de Phi que se enciende en
// morado (tokens --ap-phi, los del pill de la cartera).
// ============================================================================
.ops_by_users_card {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	width: min(720px, 94vw);
	display: flex;
	flex-direction: column;
	max-height: 86vh;
	background-color: var(--ap-paper);
	border-radius: var(--ap-radius);
	overflow: hidden;
}

// Encabezado -----------------------------------------------------------------
.ops-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding: 22px 28px 18px;
	background-color: var(--ap-paper);
	border-bottom: 1px solid var(--ap-line);
}

.ops-eyebrow {
	margin: 0 0 4px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--ap-teal);
}

.ops-title {
	margin: 0;
	font-family: var(--ap-font);
	font-weight: 700;
	font-size: 21px;
	letter-spacing: -0.01em;
	color: var(--ap-ink);
}

.ops-close {
	width: 30px;
	height: 30px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: var(--ap-slate);
	font-size: 20px;
	line-height: 1;
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:hover {
		background-color: var(--ap-mist);
		color: var(--ap-ink);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Cuerpo: secciones con eyebrow separadas por hairlines ------------------------
.ops_body {
	background-color: var(--ap-paper);
	color: var(--ap-ink-2);
	padding: 6px 28px 22px;
}

.ops-section {
	padding: 18px 0;

	& + .ops-section {
		border-top: 1px solid var(--ap-line);
	}
}

.ops-label {
	margin: 0 0 12px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.ops-hint {
	margin: -8px 0 12px;
	font-size: 12.5px;
	color: var(--ap-slate);
}

// Estado vacío (sin usuario elegido) ------------------------------------------
.ops-empty {
	margin: 18px 0;
	padding: 40px 24px;
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	text-align: center;
	font-size: 13.5px;
	color: var(--ap-muted);
}

// Píldoras de selección de usuario --------------------------------------------
.users_pills {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.user_pill {
	display: inline-flex;
	align-items: center;
	padding: 7px 14px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-pill);
	background-color: var(--ap-mist);
	color: var(--ap-slate);
	font-family: var(--ap-font);
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease),
		color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);

	&:hover {
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}

	&.is-selected {
		background-color: var(--ap-ink);
		border-color: transparent;
		color: var(--ap-paper);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Switches de instrumento ------------------------------------------------------
// (nombre propio .tgl: Buefy ya define .switch a nivel global.)
.tgl-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 12px 20px;
	margin-top: 14px;
}

.tgl {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;

	input {
		position: absolute;
		opacity: 0;
		width: 0;
		height: 0;
	}

	.tgl__track {
		position: relative;
		width: 34px;
		height: 20px;
		flex-shrink: 0;
		border-radius: var(--ap-radius-pill);
		background-color: var(--ap-line-strong);
		transition: background-color 0.16s var(--ap-ease);
	}

	.tgl__thumb {
		position: absolute;
		top: 3px;
		left: 3px;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background-color: var(--ap-paper);
		box-shadow: 0 1px 2px rgba(14, 47, 52, 0.25);
		transition: transform 0.16s var(--ap-ease);
	}

	.tgl__text {
		font-size: 13.5px;
		color: var(--ap-ink-2);
	}

	input:checked + .tgl__track {
		background-color: var(--ap-teal);

		.tgl__thumb {
			transform: translateX(14px);
		}
	}

	input:focus-visible + .tgl__track {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&:hover .tgl__text {
		color: var(--ap-ink);
	}
}

// La firma: card de Phi que se enciende en morado ------------------------------
.phi-card {
	padding: 16px 18px;
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	background-color: var(--ap-mist);
	transition: background-color 0.2s var(--ap-ease), border-color 0.2s var(--ap-ease);

	&.is-on {
		border-color: rgba(123, 91, 214, 0.28);
		background-color: var(--ap-phi-wash);
	}
}

.tgl--phi {
	input:checked + .tgl__track {
		background-color: var(--ap-phi);
	}

	input:focus-visible + .tgl__track {
		outline-color: var(--ap-phi);
	}

	.tgl__text {
		font-weight: 600;
		color: var(--ap-ink);
	}
}

.phi-quotas {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 14px 20px;
	margin-top: 16px;
}

.phi-field__label {
	display: block;
	margin-bottom: 6px;
	font-size: 12.5px;
	font-weight: 600;
	color: var(--ap-slate);
}

.phi-field__max {
	margin: 5px 2px 0;
	font-size: 11.5px;
	color: var(--ap-phi-ink);
	font-variant-numeric: tabular-nums;
}

.phi-note {
	grid-column: 1 / -1;
	margin: 2px 0 0;
	font-size: 12px;
	color: var(--ap-slate);
}

// Chips de tipos de contraparte -------------------------------------------------
.cpchips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.cpchip {
	cursor: pointer;

	input {
		position: absolute;
		opacity: 0;
		width: 0;
		height: 0;
	}

	.cpchip__body {
		display: inline-flex;
		align-items: center;
		padding: 6px 13px;
		border: 1px solid var(--ap-line-strong);
		border-radius: var(--ap-radius-pill);
		background-color: var(--ap-paper);
		color: var(--ap-slate);
		font-size: 13px;
		font-weight: 500;
		transition: background-color 0.14s var(--ap-ease),
			color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);
	}

	&:hover .cpchip__body {
		border-color: var(--ap-teal);
		color: var(--ap-teal-deep);
	}

	input:checked + .cpchip__body {
		border-color: rgba(13, 125, 128, 0.35);
		background-color: var(--ap-teal-wash);
		color: var(--ap-teal-deep);
	}

	input:focus-visible + .cpchip__body {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Chrome de inputs (Buefy .input, y el de InputWithValidation compartido) -------
:deep(.input){
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	background-color: var(--ap-paper);
	color: var(--ap-ink);
	box-shadow: none;
	transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

	&::placeholder {
		color: var(--ap-muted);
	}

	&:focus,
	&:focus-visible {
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}
}

:deep(.label){
	color: var(--ap-slate);
	font-weight: 600;
	font-size: 12.5px;
}

// Pie -----------------------------------------------------------------------
.ops-foot {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 16px 28px;
	background-color: var(--ap-paper);
	border-top: 1px solid var(--ap-line);
}

.ops-foot__note {
	font-size: 12px;
	color: var(--ap-muted);
}

.ops-foot__spacer {
	flex: 1;
}

:deep(.ops-foot .button){
	font-family: var(--ap-font);
	font-weight: 600;
	border-radius: var(--ap-radius-pill);
	border: 1px solid var(--ap-line-strong);

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

:deep(.ops-foot .button.is-primary){
	background-color: var(--ap-ink);
	border-color: transparent;

	&:hover:not([disabled]) {
		background-color: var(--ap-teal-deep);
	}
}

@media (prefers-reduced-motion: reduce) {
	.ops-close,
	.user_pill,
	.tgl .tgl__track,
	.tgl .tgl__thumb,
	.phi-card,
	.cpchip .cpchip__body,
	:deep(.input){
		transition: none;
	}
}
</style>
